import { create } from 'zustand';
import { MapService } from '../services/map/mapService';
import { logger } from '../../../shared/utils/logger';
import Polygon from 'ol/geom/Polygon';
import Feature from 'ol/Feature';
import { useUIStore } from './uiStore';
import { useCategoriesStore } from './categoriesStore';

// Interfaz unificada que combina lo mejor de ambos stores
export interface UnifiedElement {
  id: string;
  storeyId: string;
  category: string;
  area: number;
  finca: string;
  computo: number;
  nombre: string;
  refcat: string; // Referencia catastral del elemento
  geometry: any;
  feature: any; // Referencia a la feature de OpenLayers
  createdAt: string;
  updatedAt: string;
  isImported: boolean; // Para distinguir elementos importados vs dibujados
}

interface UnifiedElementsState {
  elements: UnifiedElement[];
  selectedElementId: string | null;
  mapService: MapService | null;
  // Controla si debe ajustarse la vista en la próxima sincronización al mapa
  shouldFitOnNextSync: boolean;
  
  // Acciones principales
  setMapService: (mapService: MapService | null) => void;
  setSelectedElementId: (id: string | null) => void;
  addElement: (element: Omit<UnifiedElement, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateElement: (id: string, updates: Partial<UnifiedElement>) => void;
  removeElement: (id: string) => void;
  clearAllElements: () => void;
  
  // Acciones de sincronización
  syncFromMap: () => void;
  syncToMap: () => void;
  
  // Acciones de consulta
  getElementsByStorey: (storeyId: string) => UnifiedElement[];
  getElementById: (id: string) => UnifiedElement | undefined;
  getTotalElements: () => number;
  
  // Acciones de importación/exportación
  importFromGeoJSON: (data: any) => void;
  exportToGeoJSON: () => Record<string, any[]>;
  migrateLegacyFormat: (data: any) => Record<string, any[]>;
  migrateStoreyIdsToGuids: () => void;
  
  // Sincronización de cómputos con categorías
  syncComputosFromCategories: () => void;
}

export const useUnifiedElementsStore = create<UnifiedElementsState>((set, get) => ({
  elements: [],
  selectedElementId: null,
  mapService: null,
  shouldFitOnNextSync: false,

  setMapService: (mapService: MapService | null) => {
    set({ mapService });
    if (mapService) {
      // Configurar callback para cambios en el mapa
      mapService.onElementsChanged = () => {
        get().syncFromMap();
      };
      // Sincronización inicial
      setTimeout(() => {
        get().syncFromMap();
      }, 100);
    }
  },

  setSelectedElementId: (id: string | null) => {
    set({ selectedElementId: id });
  },

  addElement: (elementData) => {
    // Generar ID único para el nuevo elemento
    const elementId = `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Verificar si el elemento ya existe para evitar duplicados
    // NOTA: Solo consideramos duplicado si el nombre es no vacío y coincide.
    // Antes, los elementos con nombre vacío ('') se consideraban duplicados y se
    // terminaba sobrescribiendo el anterior al dibujar uno nuevo.
    const hasNonEmptyName = !!elementData.nombre && elementData.nombre.trim() !== '';
    const existingElement = hasNonEmptyName
      ? get().elements.find(el => 
          el.storeyId === elementData.storeyId && 
          el.category === elementData.category &&
          el.nombre === elementData.nombre
        )
      : undefined;
    
    if (existingElement) {
      logger.warn('UNIFIED_ELEMENTS_STORE', '⚠️ Elemento con mismo nombre ya existe, actualizando en lugar de añadir:', existingElement.id);
      get().updateElement(existingElement.id, elementData);
      return;
    }

    const newElement: UnifiedElement = {
      ...elementData,
      id: elementId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Valores por defecto para campos faltantes
      finca: elementData.finca || '',
      computo: elementData.computo || 100,
      nombre: elementData.nombre || '',
      refcat: elementData.refcat || '', // Valor por defecto para refcat
      isImported: elementData.isImported || false
    };

    set((state) => ({
      elements: [...state.elements, newElement]
    }));

    // Sincronizar con el mapa solo si no es una actualización
    if (!existingElement) {
      get().syncToMap();
    }
  },

  updateElement: (id, updates) => {
    set((state) => ({
      elements: state.elements.map(element =>
        element.id === id
          ? { ...element, ...updates, updatedAt: new Date().toISOString() }
          : element
      )
    }));

    // Sincronizar con el mapa
    get().syncToMap();
  },

  removeElement: (id) => {
    set((state) => ({
      elements: state.elements.filter(element => element.id !== id),
      selectedElementId: state.selectedElementId === id ? null : state.selectedElementId
    }));

    // Sincronizar con el mapa
    get().syncToMap();
  },

  clearAllElements: () => {
    set({ elements: [], selectedElementId: null });
    
    // Limpiar también el mapa
    const { mapService } = get();
    if (mapService) {
      const gui = mapService.getGUI();
      if (gui) {
        const availableStoreys = gui.getStoreyDropdownOptions();
        availableStoreys.forEach((storey: any) => {
          const layer = mapService.getOrCreateDrawLayer(storey.id);
          if (layer) {
            layer.getSource()?.clear();
          }
        });
      }
    }
  },

  syncFromMap: () => {
    const { mapService } = get();
    if (!mapService) return;

    try {
      const elements: UnifiedElement[] = [];
      
      const layers = mapService.getLayers();
      if (!layers?.drawLayers) return;
      
      Object.entries(layers.drawLayers).forEach(([storeyId, layer]) => {
        const source = layer.getSource();
        if (!source) return;
        
        const features = source.getFeatures();
        features.forEach((feature, index) => {
          const geometry = feature.getGeometry();
          if (geometry) {
            const element: UnifiedElement = {
              id: feature.get('id') || `${storeyId}-${index}`,
              storeyId: storeyId,
              category: feature.get('type') || 'desconocido',
              area: geometry.getArea(),
              finca: feature.get('finca') || '',
              computo: feature.get('computo') || 100,
              nombre: feature.get('nombre') || '',
              refcat: feature.get('refcat') || '', // AÑADIR ESTA LÍNEA CRÍTICA
              geometry: geometry,
              feature: feature,
              createdAt: feature.get('createdAt') || new Date().toISOString(),
              updatedAt: feature.get('updatedAt') || new Date().toISOString(),
              isImported: feature.get('isImported') || false
            };
            elements.push(element);
          }
        });
      });

      set({ elements });
      logger.log('UNIFIED_ELEMENTS_STORE', '🔄 UnifiedElementsStore: Sincronizado desde mapa:', elements.length, 'elementos');
      
      // Sincronizar cómputos con categorías después de cargar elementos
      get().syncComputosFromCategories();
    } catch (error) {
              logger.error('UNIFIED_ELEMENTS_STORE', '❌ UnifiedElementsStore: Error al sincronizar desde mapa:', error);
    }
  },

  syncToMap: () => {
    const { elements, mapService } = get();
    const shouldFit = get().shouldFitOnNextSync;
    
    try {
      if (!mapService) {
        logger.warn('UNIFIED_ELEMENTS_STORE', '⚠️ UnifiedElementsStore: MapService no disponible, no se puede sincronizar al mapa');
        return;
      }

      logger.log('UNIFIED_ELEMENTS_STORE', '🔄 UnifiedElementsStore: Sincronizando elementos al mapa...');

      // Asegurar que existen capas para cada planta
      const storeyIds = Array.from(new Set(elements.map(e => e.storeyId)));
      storeyIds.forEach((storeyId) => {
        const layer = mapService.getOrCreateDrawLayer(storeyId);
        if (!layer) {
          logger.warn('UNIFIED_ELEMENTS_STORE', `⚠️ UnifiedElementsStore: No se pudo crear/obtener la capa para la planta ${storeyId}`);
        }
      });

      // Limpiar capas existentes antes de volver a añadir (evita duplicados)
      const layers = mapService.getLayers();
      if (layers?.drawLayers) {
        Object.values(layers.drawLayers).forEach((layer) => {
          const source = layer.getSource();
          if (source) {
            // Limpiar solo si hay features para evitar problemas
            const features = source.getFeatures();
            if (features.length > 0) {
              logger.log('UNIFIED_ELEMENTS_STORE', `🧹 UnifiedElementsStore: Limpiando ${features.length} features de capa ${layer.get('name') || 'sin nombre'}`);
              source.clear();
            }
          }
        });
      }

      // Añadir elementos a las capas correspondientes
      let addedToMap = 0;
      const addedStoreys = new Set<string>();
      elements.forEach(element => {
        try {
          const layer = mapService.getOrCreateDrawLayer(element.storeyId);
          if (layer && element.feature) {
            // Actualizar propiedades de la feature
            element.feature.set('id', element.id);
            element.feature.set('type', element.category);
            element.feature.set('finca', element.finca);
            element.feature.set('computo', element.computo);
            element.feature.set('nombre', element.nombre);
            element.feature.set('refcat', element.refcat); // AÑADIR ESTA LÍNEA CRÍTICA
            element.feature.set('createdAt', element.createdAt);
            element.feature.set('updatedAt', element.updatedAt);
            element.feature.set('isImported', element.isImported);
            
                         const source = layer.getSource();
             if (source) {
               try {
                 // Verificar si la feature ya existe en el source usando múltiples métodos
                 const existingFeatureById = source.getFeatureById(element.id);
                 const allFeatures = source.getFeatures();
                 const featureExistsById = allFeatures.some(f => f.getId() === element.id);
                 const featureExistsByReference = allFeatures.includes(element.feature);
                 
                 if (existingFeatureById || featureExistsById || featureExistsByReference) {
                   logger.log('UNIFIED_ELEMENTS_STORE', `🔄 UnifiedElementsStore: Feature ${element.id} ya existe en el source, actualizando propiedades`);
                                       // Actualizar propiedades de la feature existente
                    const existingFeature = existingFeatureById || allFeatures.find(f => f.getId() === element.id) || element.feature;
                    existingFeature.set('type', element.category);
                    existingFeature.set('finca', element.finca);
                    existingFeature.set('computo', element.computo);
                    existingFeature.set('nombre', element.nombre);
                    existingFeature.set('refcat', element.refcat); // AÑADIR ESTA LÍNEA CRÍTICA
                    existingFeature.set('createdAt', element.createdAt);
                    existingFeature.set('updatedAt', element.updatedAt);
                    existingFeature.set('isImported', element.isImported);
                 } else {
                   // Añadir nueva feature
                   source.addFeature(element.feature);
                   addedToMap++;
                   logger.log('UNIFIED_ELEMENTS_STORE', `✅ UnifiedElementsStore: Elemento ${element.id} añadido a planta ${element.storeyId}`);
                 }
                 addedStoreys.add(element.storeyId);
               } catch (error) {
                 logger.error('UNIFIED_ELEMENTS_STORE', `❌ UnifiedElementsStore: Error al procesar elemento ${element.id}:`, error);
               }
             }
          } else {
            logger.warn('UNIFIED_ELEMENTS_STORE', `⚠️ UnifiedElementsStore: No se pudo añadir elemento ${element.id} - capa o feature no disponible`);
          }
        } catch (error) {
          logger.error('UNIFIED_ELEMENTS_STORE', `❌ UnifiedElementsStore: Error al añadir elemento ${element.id}:`, error);
        }
      });

      logger.log('UNIFIED_ELEMENTS_STORE', `✅ UnifiedElementsStore: Sincronizado al mapa: ${addedToMap}/${elements.length} elementos añadidos`);

      // Encadrar la vista solo si está habilitado explícitamente (p. ej., tras importación)
      if (shouldFit && addedToMap > 0) {
        try {
          const gui = mapService.getGUI();
          const map = mapService.getMap();
          // NO cambiar la planta seleccionada, usar la actual
          const targetStoreyId = gui?.getSelectedStoreyId();
          // Fit a la extensión de la capa seleccionada
          const selectedLayer = targetStoreyId ? mapService.getOrCreateDrawLayer(targetStoreyId) : null;
          const features = selectedLayer?.getSource()?.getFeatures() || [];
          if (map && features.length > 0) {
            const extent = features[0].getGeometry().getExtent().slice() as any;
            // Expandir con el resto de features
            for (let i = 1; i < features.length; i++) {
              const geom = features[i].getGeometry();
              if (geom) {
                const e = geom.getExtent();
                extent[0] = Math.min(extent[0], e[0]);
                extent[1] = Math.min(extent[1], e[1]);
                extent[2] = Math.max(extent[2], e[2]);
                extent[3] = Math.max(extent[3], e[3]);
              }
            }
            logger.log('UNIFIED_ELEMENTS_STORE', '🧭 UnifiedElementsStore: Ajustando vista al extent importado', extent);
            map.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 300, maxZoom: 20 });
          }
          // Refrescar estilos para aplicar colores de categorías
          mapService.refreshDrawLayerStyles?.();
        } catch (err) {
          console.warn('⚠️ UnifiedElementsStore: No se pudo encuadrar la vista tras la importación:', err);
        }
      }

      // Consumir el flag tras usarlo
      if (shouldFit) {
        set({ shouldFitOnNextSync: false });
      }
    } catch (error) {
      console.error('❌ UnifiedElementsStore: Error al sincronizar al mapa:', error);
    }
  },

  getElementsByStorey: (storeyId: string) => {
    const { elements } = get();
    return elements.filter(element => element.storeyId === storeyId);
  },

  getElementById: (id: string) => {
    const { elements } = get();
    return elements.find(element => element.id === id);
  },

  getTotalElements: () => {
    const { elements } = get();
    return elements.length;
  },

  importFromGeoJSON: (data: any) => {
    const { mapService } = get();
    
    try {
      logger.log('UNIFIED_ELEMENTS_STORE', '📁 UnifiedElementsStore: Iniciando importación con datos:', data);
      
      // VALIDACIÓN DE PLANTAS ANTES DE IMPORTAR
      // Obtener las plantas disponibles del IFC (ahora usando GUIDs)
      const availableStoreys: string[] = [];
      if (mapService && mapService.getGUI()) {
        const gui = mapService.getGUI();
        if (gui && gui.getStoreyDropdownOptions) {
          const storeyOptions = gui.getStoreyDropdownOptions();
          availableStoreys.push(...storeyOptions.map((s: any) => s.id)); // s.id ahora es un GUID
        }
      }
      
      logger.log('UNIFIED_ELEMENTS_STORE', '🏗️ Plantas disponibles del IFC:', availableStoreys);
      
      // Detectar el tipo de formato y extraer IDs de planta
      let processedData: Record<string, any[]> = {};
      let storeyIdsFromFile: string[] = [];
      
      if (data && typeof data === 'object') {
        // Formato GeoJSON estándar (FeatureCollection)
        if (data.type === 'FeatureCollection' && Array.isArray(data.features)) {
          logger.log('UNIFIED_ELEMENTS_STORE', '✅ UnifiedElementsStore: Detectado formato GeoJSON estándar (FeatureCollection)');
          
          // Agrupar features por planta
          const featuresByStorey: Record<string, any[]> = {};
          
          data.features.forEach((feature: any) => {
            if (feature.type === 'Feature' && feature.geometry && feature.properties) {
              const storeyId = feature.properties.planta || feature.properties.storeyId || 'default';
              if (!featuresByStorey[storeyId]) {
                featuresByStorey[storeyId] = [];
              }
              
              // Convertir al formato interno
              const internalFeature = {
                type: feature.properties.categoria || feature.properties.category || 'desconocido',
                geometry: feature.geometry,
                properties: {
                  id: feature.properties.id || feature.id || '', // Preservar ID del archivo
                  nombre: feature.properties.nombre || '',
                  finca: feature.properties.finca || '',
                  computo: feature.properties.computo || 100,
                  refcat: feature.properties.refcat || '',
                  fecha_creacion: feature.properties.fecha_creacion || '',
                  fecha_modificacion: feature.properties.fecha_modificacion || ''
                }
              };
              
              featuresByStorey[storeyId].push(internalFeature);
            }
          });
          
          processedData = featuresByStorey;
          storeyIdsFromFile = Object.keys(featuresByStorey);
          
        } else if (Object.values(data).every(value => Array.isArray(value))) {
          // Formato antiguo (plantas con arrays de features)
          logger.log('UNIFIED_ELEMENTS_STORE', '✅ UnifiedElementsStore: Detectado formato antiguo (plantas con arrays)');
          processedData = data;
          storeyIdsFromFile = Object.keys(data);
          
        } else {
          // Formato desconocido, intentar migrar
          logger.warn('UNIFIED_ELEMENTS_STORE', '⚠️ UnifiedElementsStore: Formato de datos no reconocido, intentando migrar...');
          processedData = get().migrateLegacyFormat(data);
          storeyIdsFromFile = Object.keys(processedData);
        }
      } else {
        logger.warn('UNIFIED_ELEMENTS_STORE', '⚠️ UnifiedElementsStore: Formato de datos inválido, intentando migrar...');
        processedData = get().migrateLegacyFormat(data);
        storeyIdsFromFile = Object.keys(processedData);
      }
      
      // VALIDAR QUE TODAS LAS PLANTAS DEL ARCHIVO EXISTAN EN EL IFC
      if (availableStoreys.length === 0) {
        const errorMessage = 'No hay plantas del IFC disponibles. Debe cargar un archivo IFC antes de importar elementos.';
        logger.error('UNIFIED_ELEMENTS_STORE', '❌ ' + errorMessage);
        useUIStore.getState().addNotification({
          type: 'error',
          title: 'Error de Importación',
          message: errorMessage
        });
        return;
      }
      

      // Función para encontrar un storeyId por nombre
      const findStoreyIdByName = (storeyName: string): string | null => {
        if (!mapService) return null;
        
        const gui = mapService.getGUI();
        if (!gui || !gui.getStoreyDropdownOptions) return null;
        
        const storeyOptions = gui.getStoreyDropdownOptions();
        const storey = storeyOptions.find(option => 
          option.name.toLowerCase().trim() === storeyName.toLowerCase().trim()
        );
        return storey?.id || null;
      };

      // Función para obtener el nombre de planta desde el archivo
      const getStoreyNameFromFile = (storeyId: string): string => {
        // Si es formato GeoJSON, buscar en properties
        if (data.type === 'FeatureCollection' && Array.isArray(data.features)) {
          const featureWithStorey = data.features.find((f: any) => 
            (f.properties?.planta === storeyId || f.properties?.storeyId === storeyId)
          );
          if (featureWithStorey?.properties?.nombre_planta) {
            return featureWithStorey.properties.nombre_planta;
          }
        }
        return '';
      };

      // Crear mapeo de plantas del archivo a plantas del IFC
      const storeyMapping: Record<string, string> = {};
      const unmappedStoreys: string[] = [];

      for (const storeyIdFromFile of storeyIdsFromFile) {
        // Primero intentar coincidencia directa por ID
        if (availableStoreys.includes(storeyIdFromFile)) {
          storeyMapping[storeyIdFromFile] = storeyIdFromFile;
          logger.log('UNIFIED_ELEMENTS_STORE', `✅ Coincidencia directa encontrada para planta ID: ${storeyIdFromFile}`);
        } else {
          // Si no hay coincidencia directa, intentar por nombre
          const storeyNameFromFile = getStoreyNameFromFile(storeyIdFromFile);
          if (storeyNameFromFile) {
            const matchingStoreyId = findStoreyIdByName(storeyNameFromFile);
            if (matchingStoreyId) {
              storeyMapping[storeyIdFromFile] = matchingStoreyId;
              logger.log('UNIFIED_ELEMENTS_STORE', `✅ Coincidencia por nombre encontrada: "${storeyNameFromFile}" (${storeyIdFromFile} -> ${matchingStoreyId})`);
            } else {
              unmappedStoreys.push(storeyIdFromFile);
              logger.warn('UNIFIED_ELEMENTS_STORE', `⚠️ No se encontró coincidencia para planta: "${storeyNameFromFile}" (ID: ${storeyIdFromFile})`);
            }
          } else {
            unmappedStoreys.push(storeyIdFromFile);
            logger.warn('UNIFIED_ELEMENTS_STORE', `⚠️ No se pudo obtener nombre de planta para ID: ${storeyIdFromFile}`);
          }
        }
      }

      // Si hay plantas sin mapear, mostrar error
      if (unmappedStoreys.length > 0) {
        const missingStoreysWithNames = unmappedStoreys.map(storeyId => {
          const storeyName = getStoreyNameFromFile(storeyId) || `Planta ${storeyId}`;
          return { id: storeyId, name: storeyName };
        });
        
        // Crear mensaje de error detallado
        const missingList = missingStoreysWithNames
          .map(storey => `• ${storey.name} (ID: ${storey.id})`)
          .join('\n');
        
        const errorMessage = `No se puede importar el archivo. Las siguientes plantas no existen en el IFC cargado:\n\n${missingList}\n\nCargue un archivo IFC que contenga estas plantas o corrija los nombres en el archivo a importar.`;
        
        logger.error('UNIFIED_ELEMENTS_STORE', '❌ Plantas no encontradas en IFC:', unmappedStoreys);
        logger.error('UNIFIED_ELEMENTS_STORE', '❌ Detalle de plantas faltantes:', missingStoreysWithNames);
        
        useUIStore.getState().addNotification({
          type: 'error',
          title: 'Error de Importación - Plantas No Encontradas',
          message: errorMessage
        });
        
        return; // NO CONTINUAR CON LA IMPORTACIÓN
      }

      // Aplicar el mapeo a los datos procesados
      const mappedProcessedData: Record<string, any[]> = {};
      Object.entries(processedData).forEach(([originalStoreyId, featuresData]) => {
        const mappedStoreyId = storeyMapping[originalStoreyId];
        if (mappedStoreyId) {
          mappedProcessedData[mappedStoreyId] = featuresData;
          if (originalStoreyId !== mappedStoreyId) {
            logger.log('UNIFIED_ELEMENTS_STORE', `🔄 Mapeando planta ${originalStoreyId} -> ${mappedStoreyId}`);
          }
        }
      });
      
      // Actualizar los datos procesados con el mapeo
      processedData = mappedProcessedData;
      
      logger.log('UNIFIED_ELEMENTS_STORE', '✅ Validación de plantas exitosa. Todas las plantas del archivo existen en el IFC.');
      
      const importedElements: UnifiedElement[] = [];
      
      logger.log('UNIFIED_ELEMENTS_STORE', '🔄 UnifiedElementsStore: Datos procesados:', processedData);
      
      // Mostrar lista de plantas del archivo importado
      const storeyIds = Object.keys(processedData);
      if (storeyIds.length > 0) {
        logger.log('UNIFIED_ELEMENTS_STORE', '🏗️ UNIFIED_ELEMENTS_STORE: === PLANTAS DEL ARCHIVO IMPORTADO ===');
        storeyIds.forEach((storeyId, index) => {
          const featuresCount = Array.isArray(processedData[storeyId]) ? processedData[storeyId].length : 0;
          
          // Intentar obtener el nombre de la planta si está disponible en el MapService
          let storeyName = `Planta ${storeyId}`;
          if (mapService && mapService.getGUI()) {
            const gui = mapService.getGUI();
            if (gui && gui.getStoreyDropdownOptions) {
              const storeyOptions = gui.getStoreyDropdownOptions();
              const storeyOption = storeyOptions.find(opt => opt.id === storeyId);
              if (storeyOption) {
                storeyName = storeyOption.name;
              }
            }
          }
          
          logger.log('UNIFIED_ELEMENTS_STORE', `   ${index + 1}. ID: ${storeyId} | Nombre: ${storeyName} | Elementos: ${featuresCount}`);
        });
        logger.log('UNIFIED_ELEMENTS_STORE', '🏗️ UNIFIED_ELEMENTS_STORE: ===========================================');
      } else {
        logger.log('UNIFIED_ELEMENTS_STORE', '⚠️ UNIFIED_ELEMENTS_STORE: No se encontraron plantas en el archivo importado');
      }
      
      Object.entries(processedData).forEach(([storeyId, featuresData]) => {
        logger.log('UNIFIED_ELEMENTS_STORE', `🏗️ UnifiedElementsStore: Procesando planta ${storeyId} con ${Array.isArray(featuresData) ? featuresData.length : 'datos inválidos'} elementos`);
        
        if (!Array.isArray(featuresData)) {
          logger.warn('UNIFIED_ELEMENTS_STORE', `⚠️ UnifiedElementsStore: Datos inválidos para planta ${storeyId}, saltando...`);
          return;
        }
        
        featuresData.forEach((featureData, index) => {
          try {
            logger.log('UNIFIED_ELEMENTS_STORE', `🎨 UnifiedElementsStore: Procesando feature ${index} en planta ${storeyId}:`, featureData);
            
            // Validar estructura mínima
            if (!featureData.geometry) {
              logger.warn('UNIFIED_ELEMENTS_STORE', `⚠️ UnifiedElementsStore: Feature sin geometría en planta ${storeyId}, saltando...`);
              return;
            }
            
            // Crear feature de OpenLayers
            const coordinates = featureData.geometry.coordinates || featureData.geometry;

            // Crear geometría en base al tipo (actualmente Polygon soportado)
            const geometry = new Polygon(coordinates);
            
            // Detectar si las coordenadas están en WGS84 (EPSG:4326) y convertir a Web Mercator (EPSG:3857)
            // Extraer el primer par lon/lat de las coordenadas (soportando distintos niveles de anidación)
            const getFirstLonLat = (coords: any): [number, number] | null => {
              try {
                if (!Array.isArray(coords)) return null;
                // Polygon: [ [ [lon,lat], ... ] ]
                if (Array.isArray(coords[0]) && Array.isArray(coords[0][0]) && typeof coords[0][0][0] === 'number') {
                  return [coords[0][0][0], coords[0][0][1]];
                }
                // Fallbacks para variantes erróneas
                if (Array.isArray(coords[0]) && typeof coords[0][0] === 'number') {
                  return [coords[0][0], coords[0][1]];
                }
                if (typeof coords[0] === 'number') {
                  return [coords[0], coords[1]];
                }
              } catch {}
              return null;
            };

            const firstLonLat = getFirstLonLat(coordinates);
            if (firstLonLat) {
              const [lon, lat] = firstLonLat;
              const looksLike4326 = Math.abs(lon) <= 180 && Math.abs(lat) <= 90;
              if (looksLike4326) {
                logger.log('UNIFIED_ELEMENTS_STORE', `🔄 UnifiedElementsStore: Detectado EPSG:4326 (lon=${lon}, lat=${lat}). Convirtiendo a EPSG:3857...`);
                geometry.transform('EPSG:4326', 'EPSG:3857');
              } else {
                logger.log('UNIFIED_ELEMENTS_STORE', `✅ UnifiedElementsStore: Coordenadas parecen EPSG:3857 (x=${lon}, y=${lat}).`);
              }
            } else {
              logger.warn('UNIFIED_ELEMENTS_STORE', '⚠️ UnifiedElementsStore: No se pudo determinar el EPSG de las coordenadas. Se asume EPSG:3857.');
            }
            
            const feature = new Feature(geometry);
            
            // Compatibilidad con archivos de versión anterior
            // Si no hay properties, usar valores por defecto
            const properties = featureData.properties || {};
            
            // Intentar leer el ID del archivo si existe
            // 1. Si es formato FeatureCollection (GeoJSON estándar), el ID está en properties.id
            // 2. Si es formato antiguo, verificar si properties.id existe
            // 3. Solo generar nuevo ID si no existe en el archivo
            let elementId: string;
            if (properties.id && typeof properties.id === 'string' && properties.id.trim() !== '') {
              // Usar el ID del archivo
              elementId = properties.id.trim();
              logger.log('UNIFIED_ELEMENTS_STORE', `📋 UnifiedElementsStore: Usando ID del archivo: ${elementId}`);
            } else {
              // Generar nuevo ID solo si no existe en el archivo
              elementId = `imported_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              logger.log('UNIFIED_ELEMENTS_STORE', `🆕 UnifiedElementsStore: Generando nuevo ID: ${elementId}`);
            }
            
            const element: UnifiedElement = {
              id: elementId,
              storeyId: storeyId,
              category: featureData.type || 'desconocido',
              area: geometry.getArea(),
              finca: properties.finca || '',
              computo: properties.computo || 100,
              nombre: properties.nombre || '',
              refcat: properties.refcat || '',
              geometry: geometry,
              feature: feature,
              createdAt: properties.fecha_creacion || new Date().toISOString(),
              updatedAt: properties.fecha_modificacion || new Date().toISOString(),
              isImported: true
            };

            // Configurar propiedades en la feature para compatibilidad
            feature.set('type', element.category);
            feature.set('finca', element.finca);
            feature.set('computo', element.computo);
            feature.set('nombre', element.nombre);
            feature.set('refcat', element.refcat); // AÑADIR ESTA LÍNEA CRÍTICA
            feature.set('id', element.id);

            importedElements.push(element);
            logger.log('UNIFIED_ELEMENTS_STORE', `✅ UnifiedElementsStore: Elemento añadido a la lista de importación:`, element);
          } catch (error) {
            logger.error('UNIFIED_ELEMENTS_STORE', `❌ Error al procesar feature importada en planta ${storeyId}:`, error);
          }
        });
      });

      // Añadir todos los elementos importados
      set((state) => ({
        elements: [...state.elements, ...importedElements]
      }));

      // Sincronizar con el mapa solo si está disponible
      if (mapService) {
        // Habilitar encuadre solo para esta sincronización (comportamiento deseado tras importación)
        set({ shouldFitOnNextSync: true });
        get().syncToMap();
      } else {
        logger.warn('UNIFIED_ELEMENTS_STORE', '⚠️ UnifiedElementsStore: MapService no disponible, sincronización diferida');
        // Programar sincronización cuando el MapService esté disponible
        setTimeout(() => {
          const currentMapService = get().mapService;
          if (currentMapService) {
            set({ shouldFitOnNextSync: true });
            get().syncToMap();
          }
        }, 1000);
      }
      
      logger.log('UNIFIED_ELEMENTS_STORE', '✅ UnifiedElementsStore: Importados', importedElements.length, 'elementos');
      
      // Sincronizar cómputos con categorías después de importar
      get().syncComputosFromCategories();
    } catch (error) {
              logger.error('UNIFIED_ELEMENTS_STORE', '❌ UnifiedElementsStore: Error al importar:', error);
    }
  },

  exportToGeoJSON: () => {
    const { elements, mapService } = get();
    const exportData: Record<string, any[]> = {};

    // Función para obtener el nombre de la planta
    const getStoreyName = (storeyId: string): string => {
      if (!mapService) return storeyId || '';
      
      const gui = mapService.getGUI();
      if (!gui || !gui.getStoreyDropdownOptions) return storeyId || '';
      
      const storeyOptions = gui.getStoreyDropdownOptions();
      const storey = storeyOptions.find(option => option.id === storeyId);
      return storey?.name || storeyId || '';
    };

    elements.forEach(element => {
      if (!exportData[element.storeyId]) {
        exportData[element.storeyId] = [];
      }

      const coordinates = element.geometry.getCoordinates();
      exportData[element.storeyId].push({
        type: element.category,
        geometry: {
          type: "Polygon",
          coordinates: coordinates
        },
        properties: {
          id: element.id,
          finca: element.finca || '',
          computo: element.computo || 100,
          nombre: element.nombre || '',
          refcat: element.refcat || '',
          nombre_planta: getStoreyName(element.storeyId)
        }
      });
    });

    return exportData;
  },

  migrateLegacyFormat: (data: any) => {
    // Método para migrar archivos de formato antiguo
    const migratedData: Record<string, any[]> = {};
    
    try {
      logger.log('UNIFIED_ELEMENTS_STORE', '🔄 UnifiedElementsStore: Intentando migrar datos:', data);
      
      // Obtener las plantas disponibles en el sistema
      const { mapService } = get();
      const availableStoreys: string[] = [];
      if (mapService) {
        const gui = mapService.getGUI();
        if (gui) {
          const storeyOptions = gui.getStoreyDropdownOptions();
          availableStoreys.push(...storeyOptions.map((s: any) => s.id));
        }
      }
      logger.log('UNIFIED_ELEMENTS_STORE', '🏗️ UnifiedElementsStore: Plantas disponibles en el sistema:', availableStoreys);
      
      // Función para encontrar un storeyId por nombre
      const findStoreyIdByName = (storeyName: string): string | null => {
        if (!mapService) return null;
        
        const gui = mapService.getGUI();
        if (!gui || !gui.getStoreyDropdownOptions) return null;
        
        const storeyOptions = gui.getStoreyDropdownOptions();
        const storey = storeyOptions.find(option => 
          option.name.toLowerCase().trim() === storeyName.toLowerCase().trim()
        );
        return storey?.id || null;
      };

      // Función para mapear una clave a una planta disponible
      const mapToAvailableStorey = (key: string, index: number): string => {
        // Primero intentar coincidencia directa por ID
        if (availableStoreys.includes(key)) {
          return key;
        }
        
        // Si no hay coincidencia directa, intentar por nombre
        const matchingStoreyId = findStoreyIdByName(key);
        if (matchingStoreyId) {
          logger.log('UNIFIED_ELEMENTS_STORE', `✅ Migración: Coincidencia por nombre encontrada: "${key}" -> ${matchingStoreyId}`);
          return matchingStoreyId;
        }
        
        // Si no se encuentra coincidencia, usar la primera disponible o la clave original
        const fallbackStorey = availableStoreys[index] || availableStoreys[0] || key;
        logger.warn('UNIFIED_ELEMENTS_STORE', `⚠️ Migración: No se encontró coincidencia para "${key}", usando fallback: ${fallbackStorey}`);
        return fallbackStorey;
      };
      
      // Si es un array, convertirlo al nuevo formato
      if (Array.isArray(data)) {
        const targetStorey = availableStoreys[0] || 'default';
        migratedData[targetStorey] = data.map(item => ({
          type: item.type || 'desconocido',
          geometry: item.geometry || { type: 'Polygon', coordinates: [] },
          properties: {
            finca: item.finca || '',
            computo: item.computo || 100,
            nombre: item.nombre || '',
            refcat: item.refcat || ''
          }
        }));
      }
      // Si es un objeto pero no tiene la estructura esperada, intentar migrar
      else if (typeof data === 'object' && data !== null) {
        const entries = Object.entries(data);
        entries.forEach(([key, value], index) => {
          if (Array.isArray(value)) {
            // Mapear a una planta disponible usando la lógica mejorada
            const targetStorey = mapToAvailableStorey(key, index);
            migratedData[targetStorey] = value.map(item => ({
              type: item.type || 'desconocido',
              geometry: item.geometry || { type: 'Polygon', coordinates: [] },
              properties: {
                finca: item.finca || '',
                computo: item.computo || 100,
                nombre: item.nombre || '',
                refcat: item.refcat || ''
              }
            }));
          }
        });
      }
      
      logger.log('UNIFIED_ELEMENTS_STORE', '🔄 UnifiedElementsStore: Archivo migrado del formato antiguo:', migratedData);
      return migratedData;
    } catch (error) {
              logger.error('UNIFIED_ELEMENTS_STORE', '❌ UnifiedElementsStore: Error al migrar formato antiguo:', error);
      return {};
    }
  },

  migrateStoreyIdsToGuids: () => {
    const { elements, mapService } = get();
    if (!mapService || !mapService.getGUI()) return;
    
    const gui = mapService.getGUI();
    if (!gui || !gui.getStoreyDropdownOptions) return;
    
    const storeyOptions = gui.getStoreyDropdownOptions();
    const guidToExpressIdMap: { [expressId: string]: string } = {};
    
    // Crear mapeo de expressID a GUID
    for (const option of storeyOptions) {
      if (option.guid) {
        // Buscar el expressID correspondiente al GUID
        const gui = mapService.getGUI();
        const ifcModel = gui?.ifcModel;
        if (ifcModel && ifcModel.storeys) {
          for (const expressId in ifcModel.storeys) {
            const storey = ifcModel.storeys[expressId];
            if (storey.GlobalId?.value === option.guid) {
              guidToExpressIdMap[expressId] = option.guid;
              break;
            }
          }
        }
      }
    }
    
    // Migrar elementos que usan expressID a GUID
    let migratedCount = 0;
    const updatedElements = elements.map(element => {
      if (guidToExpressIdMap[element.storeyId]) {
        migratedCount++;
        return { ...element, storeyId: guidToExpressIdMap[element.storeyId] };
      }
      return element;
    });
    
    if (migratedCount > 0) {
      set({ elements: updatedElements });
      logger.log('UNIFIED_ELEMENTS_STORE', `🔄 Migrados ${migratedCount} elementos de expressID a GUID`);
    }
  },

  // Sincronizar cómputos de elementos existentes con los valores de categories.ts
  syncComputosFromCategories: () => {
    const { elements, mapService } = get();
    if (elements.length === 0) return;

    try {
      const categoriesStore = useCategoriesStore.getState();
      let updatedCount = 0;

      const updatedElements = elements.map(element => {
        // Obtener el cómputo correcto desde el store de categorías
        const category = categoriesStore.getCategoryById(element.category);
        const correctComputo = category?.computo ?? null;

        // Actualizar si el cómputo actual es diferente al correcto
        // Esto incluye el caso donde correctComputo es null (p. ej., piscinas, zonas comunes)
        if (element.computo !== correctComputo) {
          updatedCount++;
          
          // Actualizar también la feature del mapa si existe
          if (element.feature) {
            element.feature.set('computo', correctComputo);
          }

          const currentComputoDisplay = element.computo !== null ? `${element.computo}%` : 'null';
          const correctComputoDisplay = correctComputo !== null ? `${correctComputo}%` : 'null';
          
          logger.log('UNIFIED_ELEMENTS_STORE', 
            `🔄 Actualizando cómputo de elemento ${element.id} (${element.category}): ${currentComputoDisplay} → ${correctComputoDisplay}`);

          return {
            ...element,
            computo: correctComputo,
            updatedAt: new Date().toISOString()
          };
        }

        return element;
      });

      if (updatedCount > 0) {
        set({ elements: updatedElements });
        logger.log('UNIFIED_ELEMENTS_STORE', 
          `✅ Sincronizados ${updatedCount} cómputos con valores de categories.ts`);
        
        // Sincronizar con el mapa para persistir los cambios
        get().syncToMap();
      } else {
        logger.log('UNIFIED_ELEMENTS_STORE', 
          '✅ Todos los cómputos ya están sincronizados con categories.ts');
      }
    } catch (error) {
      logger.error('UNIFIED_ELEMENTS_STORE', 
        '❌ Error al sincronizar cómputos desde categorías:', error);
    }
  }
}));
